// Offline check-in queue. Check-ins are precious (streaks, Bad Week Mode
// detection) and the patient may be on flaky rural wifi — so every check-in
// first lands in localStorage keyed by client_uuid, then flushes on `online`
// and `visibilitychange`. client_uuid is unique server-side, so a double
// flush is harmless (idempotent).

import { postCheckin, type CheckinPayload } from "./api";

const QUEUE_KEY = "phoenix.checkinQueue";

type Queue = Record<string, CheckinPayload>;

function load(): Queue {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? (JSON.parse(raw) as Queue) : {};
  } catch {
    return {};
  }
}

function save(q: Queue): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch {
    /* storage full — the entry stays in memory for this session only */
  }
}

export function enqueueCheckin(payload: CheckinPayload): void {
  const q = load();
  q[payload.client_uuid] = payload;
  save(q);
}

export function pendingCount(): number {
  return Object.keys(load()).length;
}

let flushing = false;

export async function flushQueue(): Promise<void> {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    const q = load();
    for (const [uuid, payload] of Object.entries(q)) {
      try {
        await postCheckin(payload);
        const fresh = load();
        delete fresh[uuid];
        save(fresh);
      } catch (err) {
        // 409/400 = already filed or rejected — drop it; network errors keep it.
        if (err instanceof Error && "status" in err) {
          const status = (err as { status: number }).status;
          if (isDefiniteRejection(status)) {
            const fresh = load();
            delete fresh[uuid];
            save(fresh);
            continue;
          }
        }
        break; // stop on the first transport failure; retry on next flush
      }
    }
  } finally {
    flushing = false;
  }
}

// A 4xx the server will never accept, no matter how often we retry (e.g. 400
// validation, 409 already filed). Transient statuses are carved out: 401 (bad
// token — the family may fix the token later), 408 (timeout) and 429 (rate
// limited) all deserve a retry, exactly as flushQueue treats them.
function isDefiniteRejection(status: number): boolean {
  return status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;
}

/** Try to send now; fall back to the queue when the network is away. */
export async function submitCheckin(
  payload: CheckinPayload
): Promise<"sent" | "queued" | "rejected"> {
  if (!navigator.onLine) {
    enqueueCheckin(payload);
    return "queued";
  }
  try {
    await postCheckin(payload);
    return "sent";
  } catch (err) {
    if (err instanceof Error && "status" in err) {
      const status = (err as { status: number }).status;
      // Server reached but definitively rejected — do not queue a payload it
      // will never take, and do not pretend it was sent.
      if (isDefiniteRejection(status)) return "rejected";
    }
    // Transient (network error, 5xx, 401/408/429): keep it for the flush loop.
    enqueueCheckin(payload);
    return "queued";
  }
}

export function initQueueFlush(): void {
  window.addEventListener("online", () => void flushQueue());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void flushQueue();
  });
  void flushQueue();
}
